import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { 
  MessageCircle, 
  Ticket, 
  HelpCircle, 
  Send, 
  Trash2, 
  Edit2, 
  Plus,
  Clock,
  User,
  Mail,
  Package,
  Check,
  X,
  RefreshCw,
  BookmarkPlus,
  Lightbulb,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ChatbotTab = ({ token: propToken }) => {
  const [activeTab, setActiveTab] = useState('tickets');
  const [tickets, setTickets] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  
  // Get token from prop or localStorage
  const getToken = () => propToken || localStorage.getItem('auth_token');
  
  // FAQ form
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    keywords: ''
  });

  // Fetch data
  const fetchTickets = async () => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await axios.get(`${API}/api/admin/chat/tickets`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const fetchFaqs = async () => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await axios.get(`${API}/api/admin/chat/faq`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setFaqs(response.data.faqs || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const fetchSessions = async () => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await axios.get(`${API}/api/admin/chat/sessions`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchFaqs();
    fetchSessions();
  }, [propToken]);

  // Ticket actions
  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API}/api/admin/chat/tickets/${ticketId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Ticket ${status === 'cerrado' ? 'cerrado' : 'actualizado'}`);
      fetchTickets();
      setSelectedTicket(null);
    } catch (error) {
      toast.error('Error actualizando ticket');
    }
  };

  const replyToTicket = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    try {
      await axios.post(`${API}/api/admin/chat/tickets/${selectedTicket.id}/reply`,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Respuesta enviada al cliente');
      setReplyMessage('');
      fetchTickets();
      const updated = await axios.get(`${API}/api/admin/chat/tickets`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const updatedTicket = updated.data.tickets?.find(t => t.id === selectedTicket.id);
      setSelectedTicket(updatedTicket);
    } catch (error) {
      toast.error('Error enviando respuesta');
    }
  };

  // FAQ actions
  const saveFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      toast.error('Pregunta y respuesta son requeridas');
      return;
    }

    const keywords = faqForm.keywords.split(',').map(k => k.trim()).filter(k => k);

    try {
      if (editingFaq) {
        await axios.put(`${API}/api/admin/chat/faq/${editingFaq.id}`,
          { question: faqForm.question, answer: faqForm.answer, keywords },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        toast.success('FAQ actualizada');
      } else {
        await axios.post(`${API}/api/admin/chat/faq`,
          { question: faqForm.question, answer: faqForm.answer, keywords },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        toast.success('FAQ creada');
      }
      setShowFaqForm(false);
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '', keywords: '' });
      fetchFaqs();
    } catch (error) {
      toast.error('Error guardando FAQ');
    }
  };

  const deleteFaq = async (faqId) => {
    if (!window.confirm('¿Eliminar esta FAQ?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/chat/faq/${faqId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('FAQ eliminada');
      fetchFaqs();
    } catch (error) {
      toast.error('Error eliminando FAQ');
    }
  };

  const editFaq = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      keywords: faq.keywords?.join(', ') || ''
    });
    setShowFaqForm(true);
  };

  // Convert conversation to FAQ
  const convertToFaq = (userMessage, botResponse) => {
    setFaqForm({
      question: userMessage,
      answer: botResponse,
      keywords: userMessage.split(' ').filter(w => w.length > 3).slice(0, 5).join(', ')
    });
    setShowFaqForm(true);
    setEditingFaq(null);
    setActiveTab('faq');
    toast.info('Edita la FAQ antes de guardarla');
  };

  const getStatusBadge = (status) => {
    const styles = {
      abierto: 'bg-yellow-100 text-yellow-800',
      en_proceso: 'bg-blue-100 text-blue-800',
      cerrado: 'bg-green-100 text-green-800'
    };
    const labels = {
      abierto: 'Abierto',
      en_proceso: 'En Proceso',
      cerrado: 'Cerrado'
    };
    return <Badge className={styles[status] || styles.abierto}>{labels[status] || status}</Badge>;
  };

  // Get conversation pairs (user question + bot answer)
  const getConversationPairs = (messages) => {
    const pairs = [];
    for (let i = 0; i < messages.length - 1; i++) {
      if (messages[i].role === 'user' && messages[i + 1].role === 'assistant') {
        pairs.push({
          question: messages[i].content,
          answer: messages[i + 1].content,
          timestamp: messages[i].timestamp
        });
      }
    }
    return pairs;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Chatbot & Soporte</h2>
          <p className="text-muted-foreground">Gestiona tickets, FAQs y conversaciones</p>
        </div>
        <Button variant="outline" onClick={() => { fetchTickets(); fetchFaqs(); fetchSessions(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="tickets" className="flex items-center gap-2">
            <Ticket className="h-4 w-4" />
            Tickets ({tickets.filter(t => t.status !== 'cerrado').length})
          </TabsTrigger>
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            FAQs ({faqs.length})
          </TabsTrigger>
          <TabsTrigger value="sessions" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Sesiones ({sessions.length})
          </TabsTrigger>
        </TabsList>

        {/* TICKETS TAB */}
        <TabsContent value="tickets" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Tickets List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tickets de Soporte</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  {tickets.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No hay tickets</p>
                  ) : (
                    <div className="space-y-3">
                      {tickets.map(ticket => (
                        <div 
                          key={ticket.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedTicket?.id === ticket.id ? 'border-primary bg-primary/5' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedTicket(ticket)}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">#{ticket.ticket_number} - {ticket.subject}</p>
                              <p className="text-sm text-muted-foreground">{ticket.user_name}</p>
                            </div>
                            {getStatusBadge(ticket.status)}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {ticket.user_email}
                            </span>
                            {ticket.order_number && (
                              <span className="flex items-center gap-1">
                                <Package className="h-3 w-3" />
                                #{ticket.order_number}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Ticket Detail */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {selectedTicket ? `Ticket #${selectedTicket.ticket_number}` : 'Selecciona un ticket'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedTicket ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><strong>Cliente:</strong> {selectedTicket.user_name}</div>
                      <div><strong>Email:</strong> {selectedTicket.user_email}</div>
                      <div><strong>Pedido:</strong> {selectedTicket.order_number || 'N/A'}</div>
                      <div><strong>Estado:</strong> {getStatusBadge(selectedTicket.status)}</div>
                    </div>

                    <ScrollArea className="h-[250px] border rounded-lg p-3">
                      <div className="space-y-3">
                        {selectedTicket.messages?.map((msg, idx) => (
                          <div 
                            key={idx}
                            className={`p-2 rounded-lg text-sm ${
                              msg.role === 'admin' 
                                ? 'bg-blue-50 ml-8' 
                                : msg.role === 'user' 
                                  ? 'bg-gray-100 mr-8'
                                  : 'bg-green-50 mr-8'
                            }`}
                          >
                            <p className="text-xs text-muted-foreground mb-1">
                              {msg.role === 'admin' ? '👤 Admin' : msg.role === 'user' ? '🙋 Cliente' : '🤖 Bot'}
                            </p>
                            <p>{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>

                    {selectedTicket.status !== 'cerrado' && (
                      <div className="space-y-2">
                        <Textarea
                          placeholder="Escribe tu respuesta..."
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          rows={3}
                        />
                        <div className="flex gap-2">
                          <Button onClick={replyToTicket} disabled={!replyMessage.trim()}>
                            <Send className="h-4 w-4 mr-2" />
                            Enviar Respuesta
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={() => updateTicketStatus(selectedTicket.id, 'cerrado')}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Cerrar Ticket
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    Selecciona un ticket de la lista para ver los detalles
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* FAQ TAB */}
        <TabsContent value="faq" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Preguntas Frecuentes (FAQ)</CardTitle>
                <CardDescription>El chatbot usará estas respuestas para ayudar a los usuarios</CardDescription>
              </div>
              <Button onClick={() => { setShowFaqForm(true); setEditingFaq(null); setFaqForm({ question: '', answer: '', keywords: '' }); }}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva FAQ
              </Button>
            </CardHeader>
            <CardContent>
              {showFaqForm && (
                <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <h3 className="font-medium mb-3">{editingFaq ? 'Editar FAQ' : 'Nueva FAQ'}</h3>
                  <div className="space-y-3">
                    <div>
                      <Label>Pregunta</Label>
                      <Input
                        value={faqForm.question}
                        onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                        placeholder="¿Cómo funciona el proceso de compra?"
                      />
                    </div>
                    <div>
                      <Label>Respuesta</Label>
                      <Textarea
                        value={faqForm.answer}
                        onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                        placeholder="El proceso de compra es muy sencillo..."
                        rows={4}
                      />
                    </div>
                    <div>
                      <Label>Palabras clave (separadas por coma)</Label>
                      <Input
                        value={faqForm.keywords}
                        onChange={(e) => setFaqForm({ ...faqForm, keywords: e.target.value })}
                        placeholder="compra, proceso, pedido"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={saveFaq}>Guardar</Button>
                      <Button variant="outline" onClick={() => { setShowFaqForm(false); setEditingFaq(null); }}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tip for adding FAQs from sessions */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">💡 Consejo</p>
                  <p className="text-blue-700">Ve a la pestaña <strong>"Sesiones"</strong> y haz clic en <strong>"Guardar como FAQ"</strong> en cualquier respuesta del chatbot para añadirla aquí automáticamente.</p>
                </div>
              </div>

              {faqs.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No hay FAQs. Añade preguntas frecuentes para que el chatbot pueda responder mejor.
                </p>
              ) : (
                <div className="space-y-3">
                  {faqs.map(faq => (
                    <div key={faq.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-blue-600">❓ {faq.question}</p>
                          <p className="text-sm text-gray-600 mt-1">{faq.answer}</p>
                          {faq.keywords?.length > 0 && (
                            <div className="flex gap-1 mt-2 flex-wrap">
                              {faq.keywords.map((kw, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">{kw}</Badge>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">
                            Usado {faq.usage_count || 0} veces
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => editFaq(faq)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteFaq(faq.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SESSIONS TAB */}
        <TabsContent value="sessions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sesiones de Chat</CardTitle>
              <CardDescription>Haz clic en "Guardar como FAQ" para añadir respuestas útiles a tu base de conocimientos</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                {sessions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No hay sesiones de chat</p>
                ) : (
                  <div className="space-y-4">
                    {sessions.map(session => {
                      const pairs = getConversationPairs(session.messages || []);
                      const isExpanded = selectedSession === session.id;
                      
                      return (
                        <div key={session.id} className="border rounded-lg overflow-hidden">
                          {/* Session Header */}
                          <div 
                            className="p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => setSelectedSession(isExpanded ? null : session.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <User className="h-5 w-5 text-gray-400" />
                                <div>
                                  <span className="font-medium">{session.user_name || session.user_email || 'Anónimo'}</span>
                                  <p className="text-xs text-muted-foreground">
                                    {pairs.length} preguntas · {new Date(session.updated_at).toLocaleString('es-ES')}
                                  </p>
                                </div>
                              </div>
                              {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                            </div>
                          </div>
                          
                          {/* Expanded Content - Conversation Pairs */}
                          {isExpanded && pairs.length > 0 && (
                            <div className="p-3 space-y-3 border-t">
                              {pairs.map((pair, idx) => (
                                <div key={idx} className="p-3 bg-white border rounded-lg">
                                  <div className="space-y-2">
                                    <div className="flex items-start gap-2">
                                      <span className="text-lg">🙋</span>
                                      <div className="flex-1">
                                        <p className="text-xs text-muted-foreground font-medium">Pregunta del usuario:</p>
                                        <p className="text-sm">{pair.question}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-start gap-2">
                                      <span className="text-lg">🤖</span>
                                      <div className="flex-1">
                                        <p className="text-xs text-muted-foreground font-medium">Respuesta del chatbot:</p>
                                        <p className="text-sm text-gray-600">{pair.answer}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="mt-3 flex justify-end">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        convertToFaq(pair.question, pair.answer);
                                      }}
                                      className="text-green-600 border-green-300 hover:bg-green-50"
                                    >
                                      <BookmarkPlus className="h-4 w-4 mr-1" />
                                      Guardar como FAQ
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {isExpanded && pairs.length === 0 && (
                            <div className="p-4 text-center text-muted-foreground text-sm">
                              No hay pares de pregunta-respuesta en esta sesión
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ChatbotTab;
